import { value } from '@takeshape/util';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
const title = 'OpenAI';
const serviceProvider = {
    name: 'openai',
    title,
    description: 'An API for accessing AI models developed by OpenAI.',
    categories: ['ai'],
    links: [
        { title: 'Homepage', url: 'https://openai.com/' },
        { title: 'Docs', url: '/docs/services/providers/openai' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('openai'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'OpenAI'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'openai'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'OpenAI'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'You can manage API keys for OpenAI at https://platform.openai.com/api-keys'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? title,
            namespace: formData.namespace ?? title,
            provider: 'openai',
            serviceType: 'openapi',
            authenticationType: 'bearer',
            options: {
                endpoint: 'https://api.openai.com/v1/',
                spec: 'https://raw.githubusercontent.com/takeshape/openapi-specs/813784d8fcac5b3fd4e468ce6f715b89c9fcb73f/specs/openai/v2.yml',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION
            }
        };
        return updateAuthentication(config, formData, { type: 'bearer' });
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec']),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/models')
    }),
    getAiModels() {
        return {
            generate: [
                'gpt-4.1-2025-04-14',
                'gpt-4.1',
                'gpt-4.1-mini-2025-04-14',
                'gpt-4.1-mini',
                'gpt-4.1-nano-2025-04-14',
                'gpt-4.1-nano',
                'gpt-4-0613',
                'gpt-4',
                'gpt-4-1106-preview',
                'gpt-4-turbo-2024-04-09',
                'gpt-4-turbo',
                'gpt-4-0125-preview',
                'gpt-4-turbo-preview',
                'gpt-3.5-turbo',
                'gpt-3.5-turbo-instruct',
                'gpt-3.5-turbo-instruct-0914',
                'gpt-3.5-turbo-16k',
                'gpt-3.5-turbo-1106',
                'gpt-3.5-turbo-0125',
                'gpt-4o-2024-05-13',
                'gpt-4o',
                'gpt-4o-mini-2024-07-18',
                'gpt-4o-mini',
                'gpt-4o-2024-08-06',
                'gpt-4o-2024-11-20',
                'gpt-4o-search-preview-2025-03-11',
                'gpt-4o-search-preview',
                'gpt-4o-mini-search-preview-2025-03-11',
                'gpt-4o-mini-search-preview',
                'chatgpt-4o-latest',
                'o4-mini-2025-04-16',
                'o4-mini',
                'o4-mini-deep-research-2025-06-26',
                'o4-mini-deep-research',
                'o3-2025-04-16',
                'o3',
                'o3-mini-2025-01-31',
                'o3-mini',
                'o3-pro-2025-06-10',
                'o3-pro',
                'o3-deep-research',
                'o3-deep-research-2025-06-26',
                'o1-pro-2025-03-19',
                'o1-pro',
                'o1-mini-2024-09-12',
                'o1-mini',
                'o1-2024-12-17',
                'o1',
                'codex-mini-latest',
                'davinci-002',
                'babbage-002',
                'gpt-5-2025-08-07',
                'gpt-5',
                'gpt-5-chat-latest',
                'gpt-5-mini-2025-08-07',
                'gpt-5-mini',
                'gpt-5-nano-2025-08-07',
                'gpt-5-nano'
            ],
            embedding: ['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002'],
            image: ['dall-e-3', 'dall-e-2', 'gpt-image-1'],
            other: [
                'gpt-audio',
                'gpt-audio-2025-08-28',
                'gpt-realtime',
                'gpt-realtime-2025-08-28',
                'tts-1-hd',
                'tts-1-1106',
                'tts-1-hd-1106',
                'gpt-4o-realtime-preview-2024-10-01',
                'gpt-4o-audio-preview-2024-10-01',
                'gpt-4o-audio-preview',
                'gpt-4o-realtime-preview',
                'omni-moderation-latest',
                'omni-moderation-2024-09-26',
                'gpt-4o-realtime-preview-2024-12-17',
                'gpt-4o-audio-preview-2024-12-17',
                'gpt-4o-mini-realtime-preview-2024-12-17',
                'gpt-4o-mini-audio-preview-2024-12-17',
                'gpt-4o-mini-realtime-preview',
                'gpt-4o-mini-audio-preview',
                'computer-use-preview',
                'computer-use-preview-2025-03-11',
                'gpt-4o-transcribe',
                'gpt-4o-mini-transcribe',
                'gpt-4o-mini-tts',
                'gpt-4o-realtime-preview-2025-06-03',
                'gpt-4o-audio-preview-2025-06-03',
                'tts-1',
                'whisper-1'
            ]
        };
    }
};
export default serviceProvider;
